import React, { useCallback, useEffect, useState, useRef, Children } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
interface CarouselProps {
  children: React.ReactNode;
  /** Tailwind width classes for each slide (controls the "peek" of the next card). */
  itemWidthClass?: string;
  ariaLabel?: string;
}
export function Carousel({
  children,
  itemWidthClass = 'w-[82%] sm:w-[340px]',
  ariaLabel = 'Carrusel'
}: CarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const slides = Children.toArray(children);
  const [active, setActive] = useState(0);
  const updateActive = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const children = Array.from(el.children) as HTMLElement[];
    if (children.length === 0) return;
    const center = el.scrollLeft + el.clientWidth / 2;
    let nearest = 0;
    let nearestDist = Infinity;
    children.forEach((child, i) => {
      const childCenter = child.offsetLeft + child.offsetWidth / 2;
      const dist = Math.abs(childCenter - center);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = i;
      }
    });
    setActive(nearest);
  }, []);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateActive, {
      passive: true
    });
    return () => el.removeEventListener('scroll', updateActive);
  }, [updateActive]);
  const scrollToIndex = (i: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(i, slides.length - 1));
    const child = el.children[clamped] as HTMLElement | undefined;
    if (child) {
      child.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  };
  return <div className="relative" data-id="element-268">
      {/* Prev / Next arrows */}
      <button type="button" aria-label="Anterior" onClick={() => scrollToIndex(active - 1)} disabled={active === 0} className="hidden sm:flex absolute -left-3 lg:-left-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-white shadow-md border border-[#1E2A4A]/10 text-[#1E2A4A] hover:bg-[#FBF7F0] disabled:opacity-0 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E76F51]" data-id="element-269">
        <ChevronLeft size={20} data-id="element-270" />
      </button>
      <button type="button" aria-label="Siguiente" onClick={() => scrollToIndex(active + 1)} disabled={active === slides.length - 1} className="hidden sm:flex absolute -right-3 lg:-right-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-white shadow-md border border-[#1E2A4A]/10 text-[#1E2A4A] hover:bg-[#FBF7F0] disabled:opacity-0 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E76F51]" data-id="element-271">
        <ChevronRight size={20} data-id="element-272" />
      </button>

      {/* Track */}
      <div ref={scrollRef} role="group" aria-label={ariaLabel} className="flex gap-4 overflow-x-auto -mx-4 px-4 pb-4 snap-x snap-mandatory hide-scrollbar" data-id="element-273">
        {slides.map((child, i) => <div key={i} className={`snap-center shrink-0 ${itemWidthClass} flex`} data-id="element-274">
            {child}
          </div>)}
      </div>

      {/* Dots */}
      <div className="flex justify-center items-center gap-2 mt-3" data-id="element-275">
        {slides.map((_, i) => <button key={i} type="button" aria-label={`Ir al elemento ${i + 1}`} aria-current={active === i} onClick={() => scrollToIndex(i)} className={`h-2 rounded-full transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E76F51] ${active === i ? 'w-6 bg-[#E76F51]' : 'w-2 bg-[#1E2A4A]/20 hover:bg-[#1E2A4A]/40'}`} data-id="element-276" />)}
      </div>
    </div>;
}